// components/settings/surfox-settings/sections/ApiKeyManager.jsx
// API key management for Zapier / external integrations.
// Lets users generate, view (masked), and revoke API keys.

import React, { useState, useEffect } from 'react';
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  AlertTriangle,
  Eye,
  EyeOff,
  Shield,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import supabase from '../../../../lib/supabaseClient';

// supabase-js hides the server's JSON error body inside error.context (a Response).
async function describeFnError(err, fallback) {
  try {
    const body = await err?.context?.json?.();
    if (body?.error) return body.error;
  } catch { /* not a function HTTP error */ }
  return err?.message && !/non-2xx/i.test(err.message) ? err.message : fallback;
}

const isKeyExpired = (k) => !!k.expires_at && new Date(k.expires_at).getTime() <= Date.now();

// "Never" for Full keys, otherwise the date plus days left ("in 12 days").
const describeExpiry = (k) => {
  if (!k.expires_at) return 'Never';
  const ms = new Date(k.expires_at).getTime() - Date.now();
  const date = new Date(k.expires_at).toLocaleDateString();
  if (ms <= 0) return `Expired ${date}`;
  const days = Math.ceil(ms / 86400000);
  return `${date} (in ${days} day${days === 1 ? '' : 's'})`;
};

const describeScope = (k) =>
  k.scope === 'read' ? 'Read-only' : k.scope === 'lead_write' ? 'Lead write' : 'Full access';

const SCOPE_OPTIONS = [
  { value: 'read', label: 'Read-only', help: 'Can look up leads and campaigns. Cannot change anything. Limited to 60 requests a minute. Expires after 90 days.' },
  { value: 'full', label: 'Full access', help: 'Can read and change leads and hook up Zapier. Does not expire. Use this for Zapier, Follow Up Boss and Odoo.' },
];

// ============================================================================
// GENERATE KEY MODAL
// ============================================================================

const GenerateKeyModal = ({ isOpen, onClose, onGenerated }) => {
  const [name, setName] = useState('Zapier Integration');
  const [scope, setScope] = useState('read');
  const [generating, setGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [generatedExpiry, setGeneratedExpiry] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setError('');
    setGenerating(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('generate-api-key', {
        body: { name: name.trim() || 'Zapier Integration', scope },
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      setGeneratedKey(data.api_key);
      setGeneratedExpiry(data.expires_at || null);
      onGenerated?.();
    } catch (err) {
      setError(err.message || 'Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedKey) return;
    await navigator.clipboard.writeText(generatedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    const didGenerate = !!generatedKey;
    setName('Zapier Integration');
    setScope('read');
    setGeneratedKey(null);
    setGeneratedExpiry(null);
    setCopied(false);
    setError('');
    onClose(didGenerate);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Key className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {generatedKey ? 'API Key Created' : 'Generate API Key'}
              </h3>
              <p className="text-sm text-slate-500">
                {generatedKey
                  ? 'Copy your key now — it will not be shown again'
                  : 'Create a key for Zapier or external integrations'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {!generatedKey ? (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Key Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Zapier Integration"
                  maxLength={100}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-xs text-slate-500 mt-1">
                  A friendly name to identify this key later.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Access
                </label>
                <div className="space-y-2">
                  {SCOPE_OPTIONS.map((o) => (
                    <label
                      key={o.value}
                      className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer ${
                        scope === o.value ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="api-key-scope"
                        value={o.value}
                        checked={scope === o.value}
                        onChange={() => setScope(o.value)}
                        className="mt-1"
                      />
                      <span>
                        <span className="block text-sm font-medium text-slate-900">{o.label}</span>
                        <span className="block text-xs text-slate-500">{o.help}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {error && (
                <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">
                      Copy this key now. It won't be shown again.
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      Store it securely. If you lose it, you'll need to generate a new one.
                      {generatedExpiry && ` This key expires on ${new Date(generatedExpiry).toLocaleDateString()}.`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-slate-900 rounded-lg">
                  <code className="text-sm font-mono text-emerald-400 break-all select-all block">
                    {generatedKey}
                  </code>
                </div>
                <button
                  onClick={handleCopy}
                  className={`w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                    copied
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Copied to clipboard!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>Copy API Key</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-slate-100 flex items-center justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            {generatedKey ? 'Done' : 'Cancel'}
          </button>
          {!generatedKey && (
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Key className="w-4 h-4" />
              )}
              <span>{generating ? 'Generating...' : 'Generate Key'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// REVOKE CONFIRMATION MODAL
// ============================================================================

const RevokeConfirmModal = ({ isOpen, keyName, onConfirm, onCancel, revoking, error, assistant = false }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-sm w-full">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">{assistant ? 'Remove assistant' : 'Revoke API Key'}</h3>
          </div>
          {assistant ? (
            <p className="text-sm text-slate-600">
              Remove <strong>{keyName}</strong>? It will stop being able to read your SurFox data immediately.
              This cannot be undone; to connect it again, it has to sign in again.
            </p>
          ) : (
            <p className="text-sm text-slate-600">
              Are you sure you want to revoke <strong>{keyName}</strong>? Any integrations using this key
              will stop working immediately. This cannot be undone.
            </p>
          )}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg mt-3">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-100 flex items-center justify-end space-x-3">
          <button
            onClick={onCancel}
            disabled={revoking}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={revoking}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
          >
            {revoking ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            <span>{revoking ? (assistant ? 'Removing...' : 'Revoking...') : (assistant ? 'Remove assistant' : 'Revoke Key')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ACTIVITY (the call log): what each API key read or did. No message text, no phone
// numbers, no emails are ever in the log; a lead appears only as a link.
// ============================================================================

const ENDPOINT_LABELS = {
  'zapier-lead-conversation': 'Read a lead\'s conversation',
  'zapier-lead-search': 'Looked up a lead',
  'zapier-leads': { GET: 'Listed leads', POST: 'Created a lead', PUT: 'Updated a lead' },
  'inbound-leads': { POST: 'Received a lead', PUT: 'Updated a lead', GET: 'Listed leads' },
  'inbound-leads-fub': 'Follow Up Boss sent an event',
  'zapier-campaigns': 'Listed campaigns',
  'zapier-custom-fields': 'Read custom field names',
  'zapier-usage': 'Checked usage',
  'zapier-auth': 'Checked the key',
  'zapier-webhooks': { POST: 'Subscribed a webhook', DELETE: 'Removed a webhook' },
  'api-zapier-lead': 'Read one lead\'s record',
  'api-zapier-test': 'Checked the key',
};

const describeCall = (e) => {
  const l = ENDPOINT_LABELS[e.endpoint];
  if (!l) return e.endpoint;
  return typeof l === 'string' ? l : (l[e.method] || `${e.endpoint} (${e.method})`);
};

const OUTCOME_LABELS = {
  revoked: 'Refused: key was revoked',
  expired: 'Refused: key expired',
  insufficient_scope: 'Refused: read-only key',
  rate_limited: 'Refused: too many requests',
};

const describeResult = (e) => {
  if (e.outcome !== 'ok') return { text: OUTCOME_LABELS[e.outcome] || `Refused (${e.outcome})`, tone: 'red' };
  if (e.status >= 500) return { text: `Error ${e.status}`, tone: 'red' };
  if (e.status >= 400) return { text: `Error ${e.status}`, tone: 'amber' };
  return { text: 'OK', tone: 'green' };
};

const TONES = {
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

const ActivityPanel = ({ keys, grants = [] }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [keyFilter, setKeyFilter] = useState('');

  const load = async (more) => {
    if (more) setLoadingMore(true); else setLoading(true);
    try {
      const body = { action: 'log', limit: 50 };
      if (keyFilter) body.key_id = keyFilter;
      if (more && nextBefore) body.before = nextBefore;
      const { data, error: fnError } = await supabase.functions.invoke('api-keys-admin', { body });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      setEntries((prev) => (more ? [...prev, ...(data?.entries || [])] : (data?.entries || [])));
      setHasMore(!!data?.has_more);
      setNextBefore(data?.next_before || null);
      setError('');
    } catch (err) {
      console.error('Error loading API key activity:', err);
      setError(await describeFnError(err, 'Could not load the activity log.'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [keyFilter]);

  return (
    <div>
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg mb-4">
        <div className="flex items-start space-x-2">
          <Shield className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">
            Everything your API keys have done in the last 30 days: what was asked, when, which key, and whether it worked.
            The log never contains message text, phone numbers or email addresses. A lead appears only as a link.
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-2 mb-3">
        <label className="text-sm text-slate-600" htmlFor="activity-key-filter">Key</label>
        <select
          id="activity-key-filter"
          value={keyFilter}
          onChange={(e) => setKeyFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-2 py-1 text-sm"
        >
          <option value="">{grants.length ? 'All keys and assistants' : 'All keys'}</option>
          {keys.map((k) => (
            <option key={k.id} value={k.id}>{k.name}{k.is_active ? '' : ' (revoked)'}</option>
          ))}
          {grants.map((g) => (
            <option key={g.id} value={g.id}>{g.client_name} (assistant){g.status === 'active' ? '' : ` (${g.status})`}</option>
          ))}
        </select>
        <button onClick={() => load(false)} className="text-sm text-indigo-600 hover:underline">Refresh</button>
      </div>

      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-8 px-4 border-2 border-dashed border-slate-200 rounded-xl">
          <p className="text-sm text-slate-500 mb-1">No activity yet</p>
          <p className="text-xs text-slate-400">When a key is used, it shows up here.</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">When</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Key</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">What</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600 hidden sm:table-cell">Lead</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Result</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const r = describeResult(e);
                return (
                  <tr key={e.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{new Date(e.at).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-slate-700">{e.key_name || 'Deleted key'}</td>
                    <td className="px-4 py-2.5 text-slate-900">{describeCall(e)}</td>
                    <td className="px-4 py-2.5 hidden sm:table-cell">
                      {e.lead_id ? (
                        <a href={`/lead/${e.lead_id}`} className="text-indigo-600 hover:underline font-mono text-xs">
                          {String(e.lead_id).slice(0, 8)}
                        </a>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${TONES[r.tone]}`}>
                        {r.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && !loading && (
        <div className="text-center mt-3">
          <button
            onClick={() => load(true)}
            disabled={loadingMore}
            className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:opacity-50"
          >
            {loadingMore ? 'Loading...' : 'Show older activity'}
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CONNECTED ASSISTANTS: grants made through the sign-in screen (read-only, 90 days).
// Shown only when the account has at least one, so nothing changes for an account that never connected one.
// ============================================================================

const grantStatusStyle = {
  active: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  expired: 'bg-slate-100 text-slate-500',
  revoked: 'bg-slate-100 text-slate-500',
};

const AssistantsPanel = ({ grants, onRemove }) => (
  <div>
    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg mb-4">
      <div className="flex items-start space-x-2">
        <Shield className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">
          AI assistants you connected by signing in. Each can only read: your leads (including names, phone numbers and
          email addresses), your conversations with leads, your campaigns and your usage numbers. It cannot send texts or
          change anything. Access ends after 90 days, or when you remove it here. The name is set by the assistant and
          SurFox has not verified it.
        </p>
      </div>
    </div>
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-4 py-2.5 font-medium text-slate-600">Assistant</th>
            <th className="text-left px-4 py-2.5 font-medium text-slate-600 hidden sm:table-cell">Connected</th>
            <th className="text-left px-4 py-2.5 font-medium text-slate-600 hidden md:table-cell">Last used</th>
            <th className="text-left px-4 py-2.5 font-medium text-slate-600 hidden md:table-cell">Expires</th>
            <th className="text-left px-4 py-2.5 font-medium text-slate-600">Status</th>
            <th className="text-right px-4 py-2.5 font-medium text-slate-600"></th>
          </tr>
        </thead>
        <tbody>
          {grants.map((g) => (
            <tr key={g.id} className={`border-b border-slate-100 last:border-0 ${g.status === 'active' ? '' : 'opacity-50'}`}>
              <td className="px-4 py-3 font-medium text-slate-900">
                {g.client_name}
                {g.approved_by_you && <span className="ml-2 text-xs font-normal text-slate-400">connected by you</span>}
              </td>
              <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">{new Date(g.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{g.last_used_at ? new Date(g.last_used_at).toLocaleDateString() : 'Never'}</td>
              <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{describeExpiry(g)}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${grantStatusStyle[g.status] || grantStatusStyle.revoked}`}>
                  {g.status === 'active' ? 'Active' : g.status === 'expired' ? 'Expired' : 'Removed'}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                {g.status === 'active' && (
                  <button
                    onClick={() => onRemove(g)}
                    className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    title="Remove assistant"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ApiKeyManager({ onUpdate }) {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [revokeError, setRevokeError] = useState('');
  const [tab, setTab] = useState('keys');
  const [grants, setGrants] = useState([]);

  useEffect(() => {
    if (user?.tenant_id) { fetchKeys(); fetchGrants(); }
  }, [user?.tenant_id]);

  // Assistants connected through the sign-in screen. A failure here is logged and otherwise ignored: the Keys tab
  // must work whether or not this part is available, and an account with no grants sees no change at all.
  const fetchGrants = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('api-keys-admin', { body: { action: 'list_grants' } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setGrants(Array.isArray(data?.grants) ? data.grants : []);
    } catch (err) {
      console.error('Error fetching connected assistants:', err);
    }
  };

  // Keys are managed only through the api-keys-admin edge function (role checked
  // server side). The browser never reads or writes zapier_api_keys directly.
  const fetchKeys = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('api-keys-admin', {
        body: { action: 'list' },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setKeys(data?.keys || []);
      setLoadError('');
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setLoadError(await describeFnError(err, 'Could not load your API keys.'));
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setRevokeError('');

    try {
      const { data, error } = await supabase.functions.invoke('api-keys-admin', {
        body: { action: revokeTarget.assistant ? 'revoke_grant' : 'revoke', id: revokeTarget.id },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.success) throw new Error(revokeTarget.assistant ? 'The assistant was not removed.' : 'The key was not revoked.');

      setRevokeTarget(null);
      await Promise.all([fetchKeys(), fetchGrants()]);
      onUpdate?.();
    } catch (err) {
      console.error('Error revoking key:', err);
      setRevokeError(await describeFnError(err, revokeTarget?.assistant ? 'Could not remove this assistant. Please try again.' : 'Could not revoke this key. Please try again.'));
    } finally {
      setRevoking(false);
    }
  };

  const activeKeys = keys.filter((k) => k.is_active && !isKeyExpired(k));
  const revokedKeys = keys.filter((k) => !k.is_active || isKeyExpired(k));

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Key className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">API Keys</h3>
            <p className="text-sm text-slate-500">
              Manage keys for Zapier and external integrations
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowGenerate(true)}
          className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Generate Key</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-slate-200 mb-4" role="tablist">
        {[['keys', 'Keys'], ...(grants.length ? [['assistants', 'Connected assistants']] : []), ['activity', 'Activity']].map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'activity' ? (
        <ActivityPanel keys={keys} grants={grants} />
      ) : tab === 'assistants' && grants.length ? (
        <AssistantsPanel grants={grants} onRemove={(g) => setRevokeTarget({ id: g.id, name: g.client_name, assistant: true })} />
      ) : (
      <>
      {/* Info banner */}
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg mb-4">
        <div className="flex items-start space-x-2">
          <Shield className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">
            Full access keys can read and change your leads and campaigns; Read-only keys can only look things up.
            Keep them secret. Use one key per integration. A key is shown only once, when you create it.{' '}
            <a href="/api-docs" className="text-indigo-600 hover:underline inline-flex items-center">
              View API docs <ExternalLink className="w-3 h-3 ml-0.5" />
            </a>
          </p>
        </div>
      </div>

      {loadError && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-600">{loadError}</p>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
        </div>
      ) : activeKeys.length === 0 && revokedKeys.length === 0 ? (
        /* Empty state */
        <div className="text-center py-8 px-4 border-2 border-dashed border-slate-200 rounded-xl">
          <Key className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-1">No API keys yet</p>
          <p className="text-xs text-slate-400">
            Generate a key to connect SurFox to Zapier or other tools.
          </p>
        </div>
      ) : (
        /* Keys table */
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Name</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Key</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Access</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600 hidden sm:table-cell">Created</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600 hidden md:table-cell">Last Used</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600 hidden md:table-cell">Expires</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Status</th>
                <th className="text-right px-4 py-2.5 font-medium text-slate-600"></th>
              </tr>
            </thead>
            <tbody>
              {activeKeys.map((key) => (
                <tr key={key.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-900">{key.name}</td>
                  <td className="px-4 py-3">
                    <code className="px-2 py-1 bg-slate-100 rounded text-xs font-mono text-slate-600">
                      {key.key_prefix || 'sfx_...'}
                    </code>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{describeScope(key)}</td>
                  <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">
                    {new Date(key.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-slate-500 hidden md:table-cell">
                    {key.last_used_at
                      ? new Date(key.last_used_at).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{describeExpiry(key)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Active
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setRevokeTarget(key)}
                      className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                      title="Revoke key"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}

              {revokedKeys.map((key) => (
                <tr key={key.id} className="border-b border-slate-100 last:border-0 opacity-50">
                  <td className="px-4 py-3 font-medium text-slate-500">{key.name}</td>
                  <td className="px-4 py-3">
                    <code className="px-2 py-1 bg-slate-100 rounded text-xs font-mono text-slate-400">
                      {key.key_prefix || 'sfx_...'}
                    </code>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{describeScope(key)}</td>
                  <td className="px-4 py-3 text-slate-400 hidden sm:table-cell">
                    {new Date(key.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-slate-400 hidden md:table-cell">
                    {key.last_used_at
                      ? new Date(key.last_used_at).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-slate-400 hidden md:table-cell">{describeExpiry(key)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                      {key.is_active ? 'Expired' : 'Revoked'}
                    </span>
                  </td>
                  <td className="px-4 py-3"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </>
      )}

      {/* Modals */}
      <GenerateKeyModal
        isOpen={showGenerate}
        onClose={(didGenerate) => {
          setShowGenerate(false);
          // Refresh the Settings page only after Done. Doing it on create remounts the
          // page behind a spinner and destroys the modal before the key can be copied.
          if (didGenerate) onUpdate?.();
        }}
        onGenerated={fetchKeys}
      />

      <RevokeConfirmModal
        isOpen={!!revokeTarget}
        keyName={revokeTarget?.name}
        assistant={!!revokeTarget?.assistant}
        onConfirm={handleRevoke}
        onCancel={() => { setRevokeTarget(null); setRevokeError(''); }}
        revoking={revoking}
        error={revokeError}
      />
    </div>
  );
}
